import { ChevronDownIcon } from '@sketchnote/icons';
import { aiSelectStyles, generalSelectStyles } from '@sketchnote/shared-components';
import { useEffect, useMemo } from 'react';
import Select from 'react-select';
import { useWorkspaceService } from 'src/api/services/workspaces/workspace';
import { useLayout } from 'src/shared/hooks/use-layout';

import { useSidebarStore } from '../../components/sidebar/store/useSidebarStore';

export const SelectWorkspace = ({
  fileWorkspace,
  wrapperClassName,
  workspaceListClassName,
  variant = 'gray',
  aiFile = false,
}: {
  fileWorkspace?: string;
  wrapperClassName?: string;
  workspaceListClassName?: string;
  variant?: 'white' | 'gray';
  aiFile?: boolean;
}) => {
  const selectedWorkspaceId = useSidebarStore((s) => s.selectedWorkspaceId);
  const { useWorkspaces } = useWorkspaceService();
  const { userWorkspaces: workspaces } = useWorkspaces({});
  const { destinationWorkspace, setDestinationWorkspace } = useLayout();

  const otherWorkspaces = useMemo(
    () => [
      ...workspaces
        .map((obj) =>
          obj.name === 'default-workspace'
            ? {}
            : {
                label: obj.name,
                value: obj.workspaceId,
              }
        )
        .filter((x) => Object.keys(x).length !== 0),
    ],

    [workspaces]
  );

  useEffect(() => {
    if (selectedWorkspaceId) {
      const currentWorkspace = workspaces?.find((obj) => obj.workspaceId === selectedWorkspaceId);
      if (currentWorkspace)
        setDestinationWorkspace({
          label: currentWorkspace.name,
          value: currentWorkspace.workspaceId,
        });
    } else {
      workspaces.length > 0 &&
        setDestinationWorkspace({
          label: workspaces && workspaces[0].name,
          value: workspaces && workspaces[0].workspaceId,
        });
    }
  }, [selectedWorkspaceId, workspaces?.length]);

  const selectedWorkspaceOption = useMemo(() => {
    const currentWorkspace = workspaces?.find((obj) => obj.workspaceId === selectedWorkspaceId);
    if (currentWorkspace)
      return {
        label: currentWorkspace.name,
        value: currentWorkspace.workspaceId,
      };
  }, [selectedWorkspaceId, workspaces]);

  useEffect(() => {
    if (fileWorkspace) {
      const currentWorkspace = workspaces?.find((obj) => obj.workspaceId === fileWorkspace);
      if (currentWorkspace)
        setDestinationWorkspace({
          label: currentWorkspace.name,
          value: currentWorkspace.workspaceId,
        });
    }
  }, [fileWorkspace]);

  const handleSelectWorkspace = (value) => {
    setDestinationWorkspace({ label: value.label, value: value.value });
  };

  return (
    <div className="flex flex-col w-full">
      <Select
        value={destinationWorkspace.label !== 'default-workspace' && destinationWorkspace}
        options={otherWorkspaces as any}
        menuPlacement="bottom"
        styles={aiFile ? aiSelectStyles : generalSelectStyles}
        components={{
          DropdownIndicator: ChevronDownIcon as any,
          IndicatorSeparator: () => null,
        }}
        onChange={handleSelectWorkspace}
      />
      {!aiFile && (
        <div className={`flex mt-15 ${wrapperClassName} `}>
          {selectedWorkspaceOption && (
            <button
              onClick={() => {
                setDestinationWorkspace(selectedWorkspaceOption);
              }}
              className={`mr-8 px-10 ${
                destinationWorkspace.value === selectedWorkspaceOption.value
                  ? 'bg-blue-100 text-blue-500 border-blue'
                  : `${variant === 'white' ? 'bg-white' : 'bg-gray-100'} text-gray-999`
              } rounded-full border text-sm`}
            >
              {selectedWorkspaceOption.label}
            </button>
          )}

          {otherWorkspaces.slice(1, 4).map((obj, index) => {
            if (obj.value === selectedWorkspaceOption?.value) return null;
            return (
              <button
                onClick={() => {
                  if (obj.label && obj.value)
                    setDestinationWorkspace({ label: obj?.label, value: obj?.value });
                }}
                key={`${obj.value}-${index}`}
                className={`mr-8 px-10 ${workspaceListClassName} truncate ${
                  destinationWorkspace.value === obj.value
                    ? 'bg-blue-100 text-blue-500 border-blue'
                    : `${variant === 'white' ? 'bg-white' : 'bg-gray-100'} text-gray-999`
                } rounded-full border text-sm`}
              >
                {obj.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
